import { z } from 'zod';
import { platformModuleIdSchema, platformVersionedIdentifierSchema, } from './platform-module-manifest.contract.js';
export const platformModuleContributionSurfaceSchema = z.enum([
    'server',
    'worker',
    'web',
    'portal',
    'mobile',
]);
export const platformModuleContributionKindSchema = z.enum([
    'route',
    'navigation',
    'setting-editor',
    'host',
    'job',
    'subscriber',
]);
export const platformModuleContributionSettingConditionSchema = z.strictObject({
    key: z.string().min(3).max(160),
    property: z.string().min(1).max(160).nullable(),
    equals: z.union([z.string(), z.number(), z.boolean(), z.null()]),
});
export const platformModuleContributionMetadataSchema = z.strictObject({
    moduleId: platformModuleIdSchema,
    id: z.string().min(3).max(160),
    surface: platformModuleContributionSurfaceSchema,
    kind: platformModuleContributionKindSchema,
    slot: z.string().min(3).max(200).nullable(),
    capability: platformVersionedIdentifierSchema.nullable(),
    permission: z.string().min(3).max(160).nullable(),
    settingCondition: platformModuleContributionSettingConditionSchema.nullable(),
    priority: z.number().int().min(-1_000).max(1_000),
    entrypoint: z.string().min(3).max(300).nullable(),
    loading: z.enum(['eager-composition', 'lazy', 'contract-only']),
    contractVersion: z.string().regex(/^[0-9]+\.[0-9]+\.[0-9]+$/),
    emptyBehavior: z.enum([
        'not-found',
        'omit',
        'collapse',
        'inline',
        'no-op',
        'contract-only',
    ]),
    errorBehavior: z.enum([
        'platform-error',
        'propagate-to-owner',
        'continue-then-rethrow',
        'collapse',
        'inline',
        'contract-only',
    ]),
}).superRefine((metadata, context) => {
    if (metadata.loading === 'contract-only') {
        if (metadata.entrypoint === null) {
            context.addIssue({
                code: 'custom',
                message: 'A contract-only contribution must name its portable contract entrypoint.',
                path: ['entrypoint'],
            });
        }
        if (metadata.emptyBehavior !== 'contract-only' ||
            metadata.errorBehavior !== 'contract-only') {
            context.addIssue({
                code: 'custom',
                message: 'A contract-only contribution must declare contract-only empty/error behavior.',
                path: ['loading'],
            });
        }
    }
    else if (metadata.entrypoint === null) {
        context.addIssue({
            code: 'custom',
            message: 'An executable contribution must name its composition entrypoint.',
            path: ['entrypoint'],
        });
    }
});
export const platformReleaseContributionCatalogSchema = z.strictObject({
    schemaVersion: z.literal(1),
    releaseCatalogHash: z.string().regex(/^[a-f0-9]{64}$/),
    contributionCatalogHash: z.string().regex(/^[a-f0-9]{64}$/),
    contributions: z.array(platformModuleContributionMetadataSchema),
}).superRefine((catalog, context) => {
    const identities = new Set();
    for (const [index, contribution] of catalog.contributions.entries()) {
        const identity = `${contribution.moduleId}:${contribution.surface}:${contribution.kind}:${contribution.id}`;
        if (identities.has(identity)) {
            context.addIssue({
                code: 'custom',
                message: `Duplicate release contribution identity: ${identity}`,
                path: ['contributions', index, 'id'],
            });
        }
        identities.add(identity);
    }
});
export function definePlatformModuleContributionMetadata(input) {
    return platformModuleContributionMetadataSchema.parse(input);
}
export function definePlatformReleaseContributionCatalog(input) {
    const parsed = platformReleaseContributionCatalogSchema.parse(input);
    return {
        ...parsed,
        contributions: sortPlatformModuleContributionMetadata(parsed.contributions),
    };
}
export function sortPlatformModuleContributionMetadata(contributions) {
    return [...contributions].sort((left, right) => left.moduleId.localeCompare(right.moduleId) ||
        left.surface.localeCompare(right.surface) ||
        left.kind.localeCompare(right.kind) ||
        left.priority - right.priority ||
        left.id.localeCompare(right.id));
}
export function buildPlatformMobileContributionMetadata(manifests) {
    return manifests.flatMap((manifest) => {
        const entrypoint = manifest.id === 'platform'
            ? '@crest/platform-contracts/modules#platformModuleManifest.mobile'
            : `@crest/module-${manifest.id}#${manifest.id}.manifest.mobile`;
        return [
            ...manifest.mobile.routes.map((declaration) => definePlatformModuleContributionMetadata({
                moduleId: manifest.id,
                id: declaration.id,
                surface: 'mobile',
                kind: 'route',
                slot: null,
                capability: declaration.requiredCapability ?? null,
                permission: declaration.requiredPermission ?? null,
                settingCondition: null,
                priority: declaration.priority,
                entrypoint: `${entrypoint}.routes[${declaration.id}]`,
                loading: 'contract-only',
                contractVersion: manifest.version,
                emptyBehavior: 'contract-only',
                errorBehavior: 'contract-only',
            })),
            ...manifest.mobile.slots.map((declaration) => definePlatformModuleContributionMetadata({
                moduleId: manifest.id,
                id: declaration.id,
                surface: 'mobile',
                kind: 'host',
                slot: declaration.id,
                capability: declaration.requiredCapability ?? null,
                permission: declaration.requiredPermission ?? null,
                settingCondition: null,
                priority: declaration.priority,
                entrypoint: `${entrypoint}.slots[${declaration.id}]`,
                loading: 'contract-only',
                contractVersion: manifest.version,
                emptyBehavior: 'contract-only',
                errorBehavior: 'contract-only',
            })),
        ];
    });
}
//# sourceMappingURL=platform-module-contribution.contract.js.map